import { ExperiencesTableCell } from '../ExperiencesTable/components';
import { formatPricing } from '@/utils';
import {
  Button,
  ChevronDownIcon,
  Typography,
} from '@silverstein-properties/inspirelabs-ui';
import { createColumnHelper } from '@tanstack/react-table';

export interface TableData {
  id: string;
  name: string;
  members: number;
  netEarnings: number;
  refunds: number;
  platformFee: number;
  plans?: TableData[];
}

// ======================================================================================
//   TABLE COLUMN DEFINITIONS
// ======================================================================================

export const getTableColumns = (onDetailsClick?: (id: string) => void) => {
  const columnHelper = createColumnHelper<TableData>();

  return [
    columnHelper.display({
      id: 'chevron',
      cell: ({ row }) => (
        <>
          {row.getCanExpand() ? (
            <ChevronDownIcon
              width={24}
              onClick={row.getToggleExpandedHandler()}
              style={{
                cursor: 'pointer',
                transform: row.getIsExpanded()
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          ) : null}
        </>
      ),
      size: 24,
    }),
    columnHelper.accessor('name', {
      header: () => (
        <Typography variant="labelMedium" color="primary.medium">
          Name
        </Typography>
      ),
      cell: info => (
        // Plan name & cost sub-row
        <ExperiencesTableCell isSubRow={info.row.depth > 0}>
          {info.row.depth > 0 ? (
            <>
              {/* Extract plan name from the string value */}
              {info.getValue().split('~')[0]} <br />
              {/* Extract plan cost from the string value */}
              <Typography variant="body3" color="primary.medium">
                {`$${info.getValue().split('~')[1]}/mo`}
              </Typography>
            </>
          ) : (
            info.getValue()
          )}
        </ExperiencesTableCell>
      ),
      size: 192,
    }),
    columnHelper.accessor('members', {
      header: () => (
        <Typography variant="labelMedium" color="primary.medium">
          Members
        </Typography>
      ),
      cell: info => (
        <ExperiencesTableCell isSubRow={info.row.depth > 0} textAlign="right">
          {info.getValue()}
        </ExperiencesTableCell>
      ),
      size: 192,
    }),
    columnHelper.accessor('netEarnings', {
      header: () => (
        <Typography variant="labelMedium" color="primary.medium">
          Net earnings
        </Typography>
      ),
      cell: info => (
        <ExperiencesTableCell isSubRow={info.row.depth > 0} textAlign="right">
          {formatPricing(info.getValue() || 0, 2)}
        </ExperiencesTableCell>
      ),
      size: 192,
    }),
    columnHelper.accessor('platformFee', {
      header: () => (
        <Typography variant="labelMedium" color="primary.medium">
          Platform fee
        </Typography>
      ),
      cell: info => (
        <ExperiencesTableCell isSubRow={info.row.depth > 0} textAlign="right">
          {formatPricing(info.getValue() || 0, 2)}
        </ExperiencesTableCell>
      ),
      size: 192,
    }),
    columnHelper.accessor('refunds', {
      header: () => (
        <Typography variant="labelMedium" color="primary.medium">
          Refunds
        </Typography>
      ),
      cell: info => (
        <ExperiencesTableCell isSubRow={info.row.depth > 0} textAlign="right">
          {formatPricing(info.getValue() || 0, 2)}
        </ExperiencesTableCell>
      ),
      size: 192,
    }),
    columnHelper.accessor('id', {
      header: '',
      cell: info =>
        info.row.depth > 0 && onDetailsClick ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onDetailsClick(info.getValue())}
            sx={{ float: 'right' }}
          >
            Details
          </Button>
        ) : null,
      size: 80,
      enableSorting: false,
    }),
  ];
};
